import { useMemo } from 'react';
import { useOrganizationSettings } from './useOrganizationSettings';
import { 
  OrganizationType, 
  ORGANIZATION_PRESETS,
  TerminologyPreferences,
  FeatureModules,
  IndustrySpecificSettings
} from '@/types/organizationCustomizationTypes';

export interface HivePackCustomization {
  organizationType: OrganizationType | null;
  terminology: TerminologyPreferences | null;
  enabledModules: FeatureModules | null;
  industrySettings: IndustrySpecificSettings | null;
  isCustomized: boolean;
}

export const useHivePackCustomization = (organizationId?: string): HivePackCustomization => {
  const { organization } = useOrganizationSettings(organizationId);
  
  return useMemo(() => {
    if (!organization?.organization_type) {
      return {
        organizationType: null,
        terminology: null,
        enabledModules: null,
        industrySettings: null,
        isCustomized: false
      };
    }

    const organizationType = organization.organization_type as OrganizationType;
    const preset = ORGANIZATION_PRESETS[organizationType];
    
    if (!preset) {
      return {
        organizationType,
        terminology: null,
        enabledModules: null,
        industrySettings: null,
        isCustomized: false
      };
    }

    return {
      organizationType,
      terminology: preset.terminology || null,
      enabledModules: preset.enabledModules || null,
      industrySettings: preset.industrySettings || null,
      isCustomized: true
    };
  }, [organization?.organization_type]);
};

// Helper hook for getting industry-specific terminology
export const useHivePackTerminology = (organizationId?: string) => {
  const { terminology } = useHivePackCustomization(organizationId);
  
  return useMemo(() => {
    if (!terminology) {
      // Return default terminology if no HivePack is selected
      return {
        student: 'student',
        students: 'students',
        class: 'class',
        classes: 'classes',
        instructor: 'instructor',
        instructors: 'instructors',
        enrollment: 'enrollment',
        location: 'location',
        room: 'room',
        performanceEvent: 'performance',
        achievement: 'achievement',
        trial: 'trial'
      };
    }
    
    return terminology;
  }, [terminology]);
};

// Helper hook for checking if a feature is enabled in the current HivePack
export const useHivePackFeatures = (organizationId?: string) => {
  const { enabledModules } = useHivePackCustomization(organizationId);
  
  return useMemo(() => {
    const hasFeature = (feature: keyof FeatureModules): boolean => {
      if (!enabledModules) return true; // Default to enabled if no HivePack
      return enabledModules[feature] || false;
    };

    return {
      hasFeature,
      enabledModules: enabledModules || null
    };
  }, [enabledModules]);
};

export default useHivePackCustomization;